// Composer — message input with @mention autocomplete, an emoji picker, image
// attach and voice-note recording. Submits text (with mentioned ids) or media.

import { useEffect, useRef, useState } from "react";
import { MediaApi, MEDIA_MAX_BYTES } from "../hooks/useApi.js";
import { visibleCommands } from "../commands.js";
import Icon from "./Icon.jsx";
import Avatar from "./Avatar.jsx";
import Picker from "./Picker.jsx";
import { ticketType } from "../lib/ticketType.js";
import { tomorrowAt, nextMondayAt, toLocalInput } from "../lib/when.js";

const TYPE = { Audio: 1, Image: 2, Video: 3, File: 10 };
const handleFor = (name) => (name || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ".").replace(/^\.|\.$/g, "");

export default function Composer({ disabled, onSend, onSendMedia, onTyping, mentionables = [], replyTarget = null, onCancelReply,
  ticketables = [], ticketTags = [], onPickTicket, onRemoveTicket, isStaff = false, channelKind = null,
  channelables = [], channelTags = [], onPickChannel, onRemoveChannel, demoMenu = null, onEditLast, onSchedule, awayById = null }) {
  const [sched, setSched] = useState(null); // "send later" popover: { custom: "yyyy-mm-ddThh:mm" } | null
  // In the product-less Dream Team ops channel there are no tickets, so "#" references other CHANNELS
  // instead (tap the chip to jump there). Everywhere else "#" tags tickets as before.
  const hashIsChannel = channelKind === "dreamteam";
  // Slash-commands (commands.js) — feed the "/" autocomplete. The set depends on the channel kind
  // (Dream Team = cross-product ops commands); staff-only commands are hidden for clients.
  const COMMANDS = visibleCommands(isStaff, channelKind).map(([c, d]) => ({ name: c.replace(/^\//, ""), desc: d }));
  const [text, setText] = useState("");
  const [menu, setMenu] = useState({ open: false, mode: "at", query: "", index: 0 });
  const [picker, setPicker] = useState(null); // combined emoji/GIF window: null | "emoji" | "gif"
  const [attachOpen, setAttachOpen] = useState(false); // the "+" attachment menu
  const [recording, setRecording] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(null); // 0..100 while uploading (null = indeterminate/idle)
  const [pendingMedia, setPendingMedia] = useState([]); // [{ file, url, kind }] — staged images/videos + previews (send together)
  const [notice, setNotice] = useState(""); // transient composer notice (e.g. mic/permission errors)
  const inputRef = useRef(null);
  const menuRef = useRef(null); // the "/ @ #" autocomplete list (scrollable)
  const imageRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mentioned = useRef(new Map());
  const formRef = useRef(null);

  // Tutorial: force the slash / @ autocomplete open (query "") so the tour can spotlight it.
  // Filtering reads menu.query — not the input text — so we don't touch what the user typed.
  useEffect(() => {
    if (demoMenu === "slash") setMenu({ open: true, mode: "slash", query: "", index: 0 });
    else if (demoMenu === "mention") setMenu({ open: true, mode: "at", query: "", index: 0 });
    else setMenu((m) => (m.open ? { ...m, open: false } : m));
  }, [demoMenu]);

  // Keep the arrow-key-highlighted item visible: scroll the active row into view within the
  // (scrollable) menu when the selection moves past its edges. block:"nearest" scrolls only the
  // menu — not the page — and only when the item is actually off-screen.
  useEffect(() => {
    if (!menu.open) return;
    menuRef.current?.querySelector(".mention-item--active")?.scrollIntoView({ block: "nearest" });
  }, [menu.index, menu.open]);

  // Three autocomplete modes keyed off the leading char: "/" (commands, start of message only),
  // "@" (mention teammates), "#" (tag tickets). menu.mode selects which list is shown.
  const q = menu.query.toLowerCase();
  const slashMode = menu.open && menu.mode === "slash";
  const atMode = menu.open && menu.mode === "at";
  const hashMode = menu.open && menu.mode === "hash";
  const cmdMatches = slashMode ? COMMANDS.filter((c) => c.name.includes(q)).map((c) => ({ kind: "cmd", ...c })) : [];
  const memberMatches = atMode
    // TMC staff first (they're on every channel but were appended last, so an 8-item cap hid them in
    // busy channels — you had to search). Then alphabetical. The menu scrolls, so the cap is generous.
    ? mentionables.filter((u) => u.name.toLowerCase().includes(q))
        .sort((a, b) => (b.staff ? 1 : 0) - (a.staff ? 1 : 0) || (a.name || "").localeCompare(b.name || ""))
        .slice(0, 30).map((u) => ({ kind: "member", ...u }))
    : [];
  // "@all" — notify everyone in the channel. Offered at the top of the @ menu (matches empty/"all"/"everyone").
  const showAll = atMode && (q === "" || "all".includes(q) || "everyone".includes(q));
  const atMatches = atMode ? [...(showAll ? [{ kind: "all", id: 0, name: "all" }] : []), ...memberMatches] : [];
  // "/nudge <name>" — the argument IS a member name, so offer the member picker without
  // needing an "@" (people expect the list the moment they hit space after the command).
  const nudgeMode = menu.open && menu.mode === "nudge";
  const nudgeMatches = nudgeMode
    ? mentionables.filter((u) => u.name.toLowerCase().includes(q.trim()))
        .sort((a, b) => (b.staff ? 1 : 0) - (a.staff ? 1 : 0) || (a.name || "").localeCompare(b.name || ""))
        .slice(0, 30).map((u) => ({ kind: "nudgee", ...u }))
    : [];
  const ticketMatches = hashMode && !hashIsChannel
    ? ticketables.filter((t) => String(t.id).includes(q) || (t.name || "").toLowerCase().includes(q)).slice(0, 8).map((t) => ({ kind: "ticket", ...t }))
    : [];
  const channelMatches = hashMode && hashIsChannel
    ? channelables
        .filter((c) => (c.name || "").toLowerCase().includes(q) || (c.clientName || "").toLowerCase().includes(q))
        .slice()
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .slice(0, 50) // the menu scrolls; show them all (cap is just a safety limit)
        .map((c) => ({ kind: "channel", ...c }))
    : [];
  const items = slashMode ? cmdMatches : atMode ? atMatches : nudgeMode ? nudgeMatches
    : hashIsChannel ? channelMatches : ticketMatches;

  function detectMention(value, caret) {
    const upto = value.slice(0, caret);
    // "/nudge <partial name>" / "/jump <partial name>" → member picker for the argument
    // (names may contain spaces); menu.cmd remembers which command to complete.
    const prank = upto.match(/^\/(nudge|jump)\s+([\p{L}\p{N}_ ]*)$/iu);
    if (prank) { setMenu({ open: true, mode: "nudge", cmd: prank[1].toLowerCase(), query: prank[2], index: 0 }); return; }
    // Commands only trigger at the very start of the message (that's where they execute).
    const cmd = upto.match(/^\/([\p{L}\p{N}_-]*)$/u);
    if (cmd) { setMenu({ open: true, mode: "slash", query: cmd[1], index: 0 }); return; }
    const at = upto.match(/(?:^|\s)@([\p{L}\p{N}_]*)$/u);
    if (at) { setMenu({ open: true, mode: "at", query: at[1], index: 0 }); return; }
    const hash = upto.match(/(?:^|\s)#([\p{L}\p{N}_-]*)$/u);
    if (hash) { setMenu({ open: true, mode: "hash", query: hash[1], index: 0 }); return; }
    setMenu((s) => (s.open ? { ...s, open: false } : s));
  }
  function onChange(e) {
    setText(e.target.value);
    onTyping?.();
    detectMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
  }
  function pick(item) {
    const input = inputRef.current;
    const caret = input?.selectionStart ?? text.length;
    // Channel pick (Dream Team "#"): insert the "#ChannelName " token into the text like an @mention,
    // so it reads inline in the sent message (resolved to a tappable chip on render). No armed chip.
    if (item.kind === "channel") {
      const token = `#${item.name} `;
      const before = text.slice(0, caret).replace(/#([\p{L}\p{N}_-]*)$/u, token);
      const next = before + text.slice(caret);
      setText(next);
      setMenu({ open: false, mode: "at", query: "", index: 0 });
      requestAnimationFrame(() => { input?.focus(); input?.setSelectionRange(before.length, before.length); });
      return;
    }
    // Ticket pick: strip the "#query" token (the ticket chip above the composer carries the reference).
    if (item.kind === "ticket") {
      const before = text.slice(0, caret).replace(/#([\p{L}\p{N}_-]*)$/u, "");
      const next = before + text.slice(caret);
      onPickTicket?.(item);
      setText(next);
      setMenu({ open: false, mode: "at", query: "", index: 0 });
      requestAnimationFrame(() => { input?.focus(); input?.setSelectionRange(before.length, before.length); });
      return;
    }
    // Nudge/jump target: the whole message becomes "/<cmd> <name>" — ready to send.
    if (item.kind === "nudgee") {
      const next = `/${menu.cmd || "nudge"} ${item.name} `;
      setText(next);
      setMenu({ open: false, mode: "at", query: "", index: 0 });
      requestAnimationFrame(() => { input?.focus(); input?.setSelectionRange(next.length, next.length); });
      return;
    }
    // Command: replace the leading "/query" (commands live at the start of the message).
    if (item.kind === "cmd") {
      const before = text.slice(0, caret).replace(/^\/([\p{L}\p{N}_-]*)$/u, `/${item.name} `);
      const next = before + text.slice(caret);
      setText(next);
      setMenu({ open: false, mode: "at", query: "", index: 0 });
      requestAnimationFrame(() => { input?.focus(); input?.setSelectionRange(before.length, before.length); });
      return;
    }
    // "@all" and member picks both insert an @token; @all notifies everyone (resolved server-side).
    const token = `@${item.name} `;
    const before = text.slice(0, caret).replace(/@([\p{L}\p{N}_]*)$/u, token);
    const next = before + text.slice(caret);
    if (item.kind === "member") mentioned.current.set(item.name, item.id);
    setText(next);
    setMenu({ open: false, mode: "at", query: "", index: 0 });
    requestAnimationFrame(() => { input?.focus(); input?.setSelectionRange(before.length, before.length); });
  }
  function onKeyDown(e) {
    if (menu.open && items.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMenu((s) => ({ ...s, index: (s.index + 1) % items.length })); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMenu((s) => ({ ...s, index: (s.index - 1 + items.length) % items.length })); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(items[menu.index]); return; }
      if (e.key === "Escape") { setMenu((s) => ({ ...s, open: false })); return; }
    }
    // Empty composer + ArrowUp → edit your last message (Slack/Discord style).
    if (e.key === "ArrowUp" && !text && !replyTarget && !pendingMedia.length && onEditLast) {
      e.preventDefault();
      onEditLast();
      return;
    }
    // Enter sends; Shift+Enter inserts a newline (the textarea's default). Skip while an IME is
    // composing so Enter can commit a candidate without sending.
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      submit(e);
    }
  }
  // Auto-grow the textarea with its content (up to a cap; then it scrolls). When empty, leave the
  // height to CSS (one row) — measuring scrollHeight on an empty textarea wrongly includes the
  // wrapped placeholder on some mobile browsers, which inflated the composer to several rows.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (!text) { el.style.height = ""; return; }
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [text]);
  function insertEmoji(em) {
    // Keep the picker open (Discord-style) so several emoji can be added in a row.
    const input = inputRef.current;
    const caret = input?.selectionStart ?? text.length;
    setText(text.slice(0, caret) + em + text.slice(caret));
    requestAnimationFrame(() => { input?.focus(); const p = caret + em.length; input?.setSelectionRange(p, p); });
  }

  // Reject over-limit files up front with a clear message instead of firing a doomed upload that
  // fails silently (the old behaviour — hence "looks stuck"). iPhone videos in particular are large.
  function tooBig(file) {
    if (!file || file.size <= MEDIA_MAX_BYTES) return false;
    const mb = Math.round(file.size / (1024 * 1024));
    const isVid = (file.type || "").startsWith("video/");
    setNotice(isVid
      ? `That video is ${mb} MB — the limit is 100 MB. Trim the clip, or lower the recording quality (iPhone: Settings › Camera › Record Video), then try again.`
      : `That file is ${mb} MB — the limit is 100 MB. Please send a smaller file.`);
    return true;
  }

  async function sendMedia(file, type) {
    setMediaBusy(true);
    try { onSendMedia?.(type, (await MediaApi.upload(file)).url); }
    catch (err) { setNotice(err?.message || "Upload failed — please try again."); }
    finally { setMediaBusy(false); }
  }
  // Stage an image/video with a local preview; queued (append) so several can be staged and sent
  // together on submit. Each carries its own object-URL, used as its React key + revoked on remove.
  function stageMediaFile(file) {
    if (!file || tooBig(file)) return;
    const kind = file.type.startsWith("video/") ? "video" : "image";
    setPendingMedia((prev) => [...prev, { file, kind, url: URL.createObjectURL(file) }]);
    inputRef.current?.focus();
  }
  // A non-media attachment (PDF, HTML doc, zip, …) has no inline preview to stage, so upload and
  // send it straight away as a File message (its filename rides along as the message content).
  async function sendFile(file) {
    if (tooBig(file)) return;
    setMediaBusy(true); setUploadPct(0);
    try { const up = await MediaApi.uploadDirect(file, (f) => setUploadPct(Math.round(f * 100))); onSendMedia?.(TYPE.File, up.url, up.name || file.name); }
    catch (err) { setNotice(err?.message || "Upload failed — please try again."); }
    finally { setMediaBusy(false); setUploadPct(null); }
  }
  // Images/videos get staged previews + a shared caption; everything else sends as a file attachment.
  function addFiles(files) {
    for (const file of files) {
      if (file.type.startsWith("image/") || file.type.startsWith("video/")) stageMediaFile(file);
      else sendFile(file);
    }
  }
  function onPickMedia(e) {
    const files = Array.from(e.target.files || []);
    if (imageRef.current) imageRef.current.value = "";
    addFiles(files);
  }
  // Drag & drop files anywhere on the chat pane (or the thread panel, for the thread's composer):
  // same path as the "+" picker. The zone gets .is-dropping while files hover over it.
  const dropRef = useRef(null);
  dropRef.current = disabled || recording ? null : addFiles;
  useEffect(() => {
    const zone = formRef.current?.closest(".thread-panel, .chat__main");
    if (!zone) return;
    let depth = 0;
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
    const over = (e) => { if (!hasFiles(e) || !dropRef.current) return; e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; };
    const enter = (e) => { if (!hasFiles(e) || !dropRef.current) return; e.stopPropagation(); depth++; zone.classList.add("is-dropping"); };
    const leave = (e) => { if (!hasFiles(e)) return; e.stopPropagation(); if (--depth <= 0) { depth = 0; zone.classList.remove("is-dropping"); } };
    const drop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault(); e.stopPropagation();
      depth = 0; zone.classList.remove("is-dropping");
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length && dropRef.current) dropRef.current(files);
    };
    zone.addEventListener("dragenter", enter);
    zone.addEventListener("dragover", over);
    zone.addEventListener("dragleave", leave);
    zone.addEventListener("drop", drop);
    return () => {
      zone.removeEventListener("dragenter", enter);
      zone.removeEventListener("dragover", over);
      zone.removeEventListener("dragleave", leave);
      zone.removeEventListener("drop", drop);
      zone.classList.remove("is-dropping");
    };
  }, []);
  // Paste image(s) (e.g. screenshots) from the clipboard → stage them like picked files. Stages EVERY
  // image on the clipboard and appends to anything already staged, so multiple pastes accumulate.
  // Text pastes fall through to the browser's default handling.
  function onPaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    let staged = false;
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const file = it.getAsFile();
        if (file) { stageMediaFile(file); staged = true; }
      }
    }
    if (staged) e.preventDefault();
  }
  function removePendingMedia(url) {
    setPendingMedia((prev) => { const m = prev.find((x) => x.url === url); if (m?.url) URL.revokeObjectURL(m.url); return prev.filter((x) => x.url !== url); });
  }
  async function toggleRecording() {
    if (recording) { recorderRef.current?.stop(); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setNotice("Voice notes aren’t supported on this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Samsung/Android often can't record webm — pick the first mime type this device
      // actually supports (mp4/aac on Samsung), else let the browser choose its default.
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"];
      const mimeType = candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data.size) chunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        setRecording(false);
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mimeType || "audio/webm" });
        const ext = blob.type.includes("mp4") || blob.type.includes("aac") ? "m4a" : "webm";
        await sendMedia(new File([blob], `voice-note.${ext}`, { type: blob.type }), TYPE.Audio);
      };
      rec.onerror = () => { setRecording(false); setNotice("Recording failed — try again."); };
      recorderRef.current = rec; rec.start(); setRecording(true); setNotice("");
    } catch (e) {
      setRecording(false);
      setNotice(
        e?.name === "NotAllowedError" || e?.name === "SecurityError"
          ? "Microphone blocked — allow mic access in your browser settings."
          : "Couldn’t start recording — check microphone permissions.",
      );
    }
  }

  async function submit(e) {
    e.preventDefault();
    const value = text.trim();
    const resetInput = () => { setText(""); mentioned.current.clear(); setMenu({ open: false, mode: "at", query: "", index: 0 }); setPicker(null); setAttachOpen(false); };

    // Staged image(s)/video(s) → upload all (in parallel), then send each as its own message in the
    // order they were staged. The typed text rides as the caption on the LAST one so it reads beneath
    // the set. Snapshot the queue first so a paste mid-send doesn't get dropped or double-sent.
    if (pendingMedia.length > 0) {
      if (mediaBusy) return;
      const queue = pendingMedia;
      setMediaBusy(true); setUploadPct(0);
      // Aggregate progress across the parallel uploads: sum each file's bytes-so-far over total bytes.
      const totalBytes = queue.reduce((s, m) => s + (m.file.size || 0), 0);
      const loaded = new Array(queue.length).fill(0);
      const bump = (i, frac) => {
        loaded[i] = frac * (queue[i].file.size || 0);
        const sum = loaded.reduce((a, b) => a + b, 0);
        if (totalBytes) setUploadPct(Math.round((sum / totalBytes) * 100));
      };
      try {
        const uploaded = await Promise.all(queue.map((m, i) => MediaApi.uploadDirect(m.file, (f) => bump(i, f))));
        queue.forEach((m, i) => {
          const caption = i === queue.length - 1 ? value : "";
          onSendMedia?.(m.kind === "video" ? TYPE.Video : TYPE.Image, uploaded[i].url, caption);
        });
        // Remove exactly the sent items (revoking their previews); keep anything staged mid-send.
        const sent = new Set(queue.map((m) => m.url));
        setPendingMedia((prev) => { queue.forEach((m) => { if (m.url) URL.revokeObjectURL(m.url); }); return prev.filter((x) => !sent.has(x.url)); });
        resetInput();
      } catch (err) { setNotice(err?.message || "Upload failed — please try again."); }
      finally { setMediaBusy(false); setUploadPct(null); }
      return;
    }

    // Allow a message that's only ticket/channel tags (no text); the parent attaches the tags.
    if (!value && ticketTags.length === 0 && channelTags.length === 0) return;
    onSend(value, mentionIds(value));
    resetInput();
  }

  // Resolve @mentions two ways: names picked from the autocomplete, AND names typed
  // manually that match a channel member (longest-first so "@Jane Doe" wins over "@Jane").
  function mentionIds(value) {
    const ids = new Set();
    for (const [name, id] of mentioned.current) if (value.includes(`@${name}`)) ids.add(id);
    for (const u of [...mentionables].sort((a, b) => b.name.length - a.name.length))
      if (value.includes(`@${u.name}`)) ids.add(u.id);
    return [...ids];
  }

  // "Send later": hand the text to the parent with a time, then clear like a send.
  async function scheduleAt(date) {
    const value = text.trim();
    if (!value || !onSchedule || Number.isNaN(date.getTime())) return;
    const ok = await onSchedule(value, mentionIds(value), date.toISOString());
    if (ok === false) return; // parent showed why; keep the text
    setText(""); mentioned.current.clear(); setSched(null);
  }

  return (
    <form ref={formRef} className="composer" data-tour="composerzone" onSubmit={submit}>
      {notice && (
        <div className="composer__notice" role="alert">
          <Icon name="warning" className="composer__notice-ic" />
          <span className="composer__notice-tx">{notice}</span>
          <button type="button" className="composer__reply-x" title="Dismiss" onClick={() => setNotice("")}><Icon name="close" className="ic--sm" /></button>
        </div>
      )}
      {(replyTarget || ticketTags.length > 0 || channelTags.length > 0) && (
        <div className="composer__above">
          {(ticketTags.length > 0 || channelTags.length > 0) && (
            <div className="composer__tags">
              {ticketTags.map((t) => (
                <span key={`t-${t.id}`} className={`composer__tag composer__tag--${ticketType(t)}`}>
                  <span className="composer__tag-ic"><Icon name={ticketType(t)} className="ic--sm" /></span>
                  <span className="composer__tag-id">#{t.id}</span>
                  <span className="composer__tag-name">{t.name}</span>
                  <button type="button" className="composer__tag-x" title="Remove tag" onClick={() => onRemoveTicket?.(t.id)}><Icon name="close" className="ic--sm" /></button>
                </span>
              ))}
              {channelTags.map((c) => (
                <span key={`ch-${c.id}`} className="composer__tag composer__tag--channel">
                  <span className="composer__tag-ic">#</span>
                  <span className="composer__tag-name">{c.name}</span>
                  <button type="button" className="composer__tag-x" title="Remove reference" onClick={() => onRemoveChannel?.(c.id)}><Icon name="close" className="ic--sm" /></button>
                </span>
              ))}
            </div>
          )}
          {replyTarget && (
            <div className="composer__reply">
              <Icon name="reply" className="composer__reply-ic" />
              <span className="composer__reply-tx">
                Replying to <b className="composer__reply-name">{replyTarget.senderName}</b>
                {replyTarget.snippet ? <span className="composer__reply-snip"> · {replyTarget.snippet}</span> : null}
              </span>
              <button type="button" className="composer__reply-x" title="Cancel reply" onClick={onCancelReply}><Icon name="close" className="ic--sm" /></button>
            </div>
          )}
        </div>
      )}
      {pendingMedia.length > 0 && (
        <div className="composer__attach">
          {pendingMedia.map((m) => (
            <div key={m.url} className="composer__attach-item">
              {m.kind === "video" ? <video src={m.url} muted /> : <img src={m.url} alt="" />}
              <button type="button" className="composer__attach-remove" title="Remove" onClick={() => removePendingMedia(m.url)}>
                <Icon name="close" className="ic--sm" />
              </button>
            </div>
          ))}
          <span className="composer__attach-hint">
            {mediaBusy
              ? (uploadPct != null ? `Uploading… ${uploadPct}%` : "Sending…")
              : pendingMedia.length > 1 ? `${pendingMedia.length} attachments — add a caption, then send` : "Add a caption, then send"}
          </span>
          {mediaBusy && (
            <div className="composer__attach-bar" role="progressbar" aria-valuenow={uploadPct ?? undefined} aria-valuemin={0} aria-valuemax={100}>
              <span className={`composer__attach-bar-fill ${uploadPct == null ? "is-indeterminate" : ""}`}
                    style={uploadPct != null ? { width: `${uploadPct}%` } : undefined} />
            </div>
          )}
        </div>
      )}
      {menu.open && items.length > 0 && (
        <ul className="mention-menu" ref={menuRef} data-tour={menu.mode === "slash" ? "slash" : "mentionpop"}>
          <li className="mention-menu__hint">↑↓ to navigate · ⏎ to select · esc to dismiss</li>
          {hashMode && !hashIsChannel && (
            <div className="mention-menu__section">
              <div className="mention-menu__label">Tickets</div>
              {ticketMatches.map((t, i) => (
                <li key={`t-${t.id}`} className={`mention-item ${menu.index === i ? "mention-item--active" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); pick(t); }}>
                  <span className={`mention-item__tk mention-item__tk--${t.isBug ? "bug" : "feature"}`}><Icon name={t.isBug ? "bug" : "feature"} className="ic--sm" /></span>
                  <span className="mention-item__tkid">#{t.id}</span>
                  <span className="mention-item__name">{t.name}</span>
                  {t.statusName ? <span className="mention-item__handle">{t.statusName}</span> : null}
                </li>
              ))}
            </div>
          )}
          {hashMode && hashIsChannel && (
            <div className="mention-menu__section">
              <div className="mention-menu__label">Channels</div>
              {channelMatches.map((c, i) => (
                <li key={`ch-${c.id}`} className={`mention-item ${menu.index === i ? "mention-item--active" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); pick(c); }}>
                  <span className="mention-item__tk mention-item__tk--channel">#</span>
                  <span className="mention-item__name">{c.name}</span>
                  {c.clientName ? <span className="mention-item__handle">{c.clientName}</span> : null}
                </li>
              ))}
            </div>
          )}
          {cmdMatches.length > 0 && (
            <div className="mention-menu__section">
              <div className="mention-menu__label">Commands</div>
              {cmdMatches.map((c, i) => (
                <li key={`c-${c.name}`} className={`mention-item ${menu.index === i ? "mention-item--active" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); pick(c); }}>
                  <span className="mention-item__cmd">/{c.name}</span>
                  <span className="mention-item__desc">{c.desc}</span>
                </li>
              ))}
            </div>
          )}
          {nudgeMatches.length > 0 && (
            <div className="mention-menu__section">
              <div className="mention-menu__label">{menu.cmd === "jump" ? "Scare who? 😱" : "Nudge who? 👋"}</div>
              {nudgeMatches.map((u, i) => (
                <li key={`n-${u.id}`} className={`mention-item ${menu.index === i ? "mention-item--active" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); pick(u); }}>
                  <Avatar userId={u.id} name={u.name} url={u.avatarUrl} size="sm" />
                  <span className="mention-item__name">{u.name}</span>
                  {u.staff && <span className="mention-item__tag">TMC</span>}
                </li>
              ))}
            </div>
          )}
          {atMatches.length > 0 && (
            <div className="mention-menu__section">
              <div className="mention-menu__label">Members</div>
              {atMatches.map((u, i) => (
                u.kind === "all" ? (
                  <li key="m-all" className={`mention-item ${menu.index === i ? "mention-item--active" : ""}`}
                      onMouseDown={(e) => { e.preventDefault(); pick(u); }}>
                    <span className="mention-item__all">@</span>
                    <span className="mention-item__name">all</span>
                    <span className="mention-item__handle">notify everyone here</span>
                  </li>
                ) : (
                  <li key={`m-${u.id}`} className={`mention-item ${menu.index === i ? "mention-item--active" : ""}`}
                      onMouseDown={(e) => { e.preventDefault(); pick(u); }}>
                    <Avatar userId={u.id} name={u.name} url={u.avatarUrl} size="sm" />
                    <span className="mention-item__name">{u.name}</span>
                    {u.staff && <span className="mention-item__tag">TMC</span>}
                    {awayById?.[u.id] && <span className="mention-item__away">🌴 on leave until {new Date(`${awayById[u.id]}T00:00:00`).toLocaleDateString([], { day: "numeric", month: "short" })}</span>}
                    <span className="mention-item__handle">{handleFor(u.name)}</span>
                  </li>
                )
              ))}
            </div>
          )}
        </ul>
      )}

      <Picker open={!!picker} tab={picker} onTab={setPicker} onClose={() => setPicker(null)}
              onEmoji={insertEmoji}
              onGif={(g) => { onSendMedia?.(TYPE.Image, g.url, ""); setPicker(null); }} />

      {attachOpen && (
        <>
          <div className="attach-menu__overlay" onClick={() => setAttachOpen(false)} />
          <div className="attach-menu">
            <button type="button" className="attach-menu__item"
                    onClick={() => { setAttachOpen(false); imageRef.current?.click(); }}>
              <Icon name="image" className="ic--sm" /> Upload a file
            </button>
          </div>
        </>
      )}

      {/* "+" attachment menu (Discord-style) — replaces the old direct image button */}
      <button type="button" className={`composer__icon ${attachOpen ? "is-on" : ""}`} title="Attach"
              disabled={disabled || mediaBusy || recording} onClick={() => { setAttachOpen((v) => !v); setPicker(null); }}><Icon name="plus" /></button>
      <input ref={imageRef} type="file" multiple hidden onChange={onPickMedia} />
      <button type="button" className={`composer__icon ${picker ? "is-on" : ""}`} title="Emoji & GIFs"
              disabled={disabled || recording} onClick={() => { setPicker((v) => (v ? null : "emoji")); setAttachOpen(false); }}><Icon name="smile" /></button>
      <button type="button" className={`composer__icon ${recording ? "composer__icon--rec" : ""}`}
              title={recording ? "Stop & send voice note" : "Record voice note"}
              disabled={disabled || mediaBusy} onClick={toggleRecording}><Icon name={recording ? "stop" : "mic"} /></button>

      <textarea ref={inputRef} className="composer__input" data-tour="composer" value={text} disabled={disabled || recording} rows={1}
             placeholder={recording ? "Recording…" : pendingMedia.length ? "Add a caption…" : mediaBusy ? "Uploading…" : replyTarget ? "Reply…" : "Message  ·  /  @  #"}
             onChange={onChange} onKeyDown={onKeyDown} onPaste={onPaste} />

      {onSchedule && (
        <button type="button" className={`composer__icon composer__sched ${sched ? "is-on" : ""}`} title="Send later"
                disabled={disabled || recording || !text.trim() || pendingMedia.length > 0 || text.trim().startsWith("/")}
                onClick={() => setSched((s) => (s ? null : { custom: toLocalInput(tomorrowAt(8)) }))}>🕒</button>
      )}
      {sched && (
        <div className="composer__sched-pop" role="dialog" aria-label="Send later">
          <div className="composer__sched-title">Send later</div>
          <button type="button" className="composer__sched-opt" onClick={() => scheduleAt(tomorrowAt(8))}>Tomorrow 08:00</button>
          <button type="button" className="composer__sched-opt" onClick={() => scheduleAt(nextMondayAt(8))}>Monday 08:00</button>
          <div className="composer__sched-custom">
            <input type="datetime-local" className="input" value={sched.custom} onChange={(e) => setSched({ custom: e.target.value })} />
            <button type="button" className="btn btn--primary btn--sm" disabled={!sched.custom} onClick={() => scheduleAt(new Date(sched.custom))}>Schedule</button>
          </div>
        </div>
      )}

      <button className="composer__send" type="submit" title="Send" disabled={disabled || mediaBusy || (!text.trim() && !pendingMedia.length && !ticketTags.length && !channelTags.length)}><Icon name="send" /></button>
    </form>
  );
}
